<script lang="ts">
  import { createQuery, useQueryClient } from '@tanstack/svelte-query';
  import { Plus } from 'lucide-svelte';
  import { hrLeaveApi } from '$lib/api/hr';
  import { getErrorMessage } from '$lib/api/client';
  import { toast } from '$lib/state/toast.svelte';
  import { session } from '$lib/state/session.svelte';
  import Modal from '$lib/components/ui/Modal.svelte';
  import StatusLamp from '$lib/components/ui/StatusLamp.svelte';
  import StatusBadge from '$lib/components/ui/StatusBadge.svelte';
  import Toolbar from '$lib/components/ui/Toolbar.svelte';
  import DataTable from '$lib/components/ui/DataTable.svelte';
  import RowActions from '$lib/components/ui/RowActions.svelte';
  import type { LeaveType, LeaveTypeBody } from '$lib/types';

  const queryClient = useQueryClient();
  const query = createQuery(() => ({
    queryKey: ['leave-types'],
    queryFn: () => hrLeaveApi.listTypes()
  }));

  function refresh() {
    queryClient.invalidateQueries({ queryKey: ['leave-types'] });
  }

  const emptyForm = (): LeaveTypeBody => ({ name: '', days_per_year: 0, active: true, is_deduction_default: false });

  let editing = $state<LeaveType | null>(null);
  let showForm = $state(false);
  let form = $state<LeaveTypeBody>(emptyForm());
  let saving = $state(false);
  let error = $state('');

  function openCreate() {
    editing = null;
    form = emptyForm();
    error = '';
    showForm = true;
  }

  function openEdit(lt: LeaveType) {
    editing = lt;
    form = { name: lt.name, days_per_year: lt.days_per_year, active: lt.active, is_deduction_default: lt.is_deduction_default };
    error = '';
    showForm = true;
  }

  async function save() {
    if (!form.name?.trim()) {
      error = 'Name is required.';
      return;
    }
    if (form.days_per_year == null || form.days_per_year < 0) {
      error = 'Quota (days/year) must be 0 or more.';
      return;
    }
    saving = true;
    error = '';
    try {
      if (editing) await hrLeaveApi.updateType(editing.id, form);
      else await hrLeaveApi.createType(form);
      refresh();
      showForm = false;
      toast.success(editing ? 'Leave type updated.' : 'Leave type created.');
    } catch (err) {
      error = getErrorMessage(err);
    } finally {
      saving = false;
    }
  }

  async function toggleActive(lt: LeaveType) {
    const verb = lt.active ? 'deactivate' : 'reactivate';
    if (!confirm(`${verb.charAt(0).toUpperCase() + verb.slice(1)} "${lt.name}"?`)) return;
    try {
      await hrLeaveApi.updateType(lt.id, { active: !lt.active });
      refresh();
      toast.success(`Leave type ${lt.active ? 'deactivated' : 'reactivated'}.`);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }

  async function setDefault(lt: LeaveType) {
    try {
      await hrLeaveApi.updateType(lt.id, { is_deduction_default: true });
      refresh();
      toast.success(`"${lt.name}" is now the forgot-punch-out deduction default.`);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }

  function actionsFor(lt: LeaveType) {
    const items: { label: string; onClick: () => void; danger?: boolean }[] = [];
    if (lt.active && !lt.is_deduction_default) items.push({ label: 'Set default', onClick: () => setDefault(lt) });
    items.push({ label: 'Edit', onClick: () => openEdit(lt) });
    items.push({ label: lt.active ? 'Deactivate' : 'Reactivate', onClick: () => toggleActive(lt), danger: lt.active });
    return items;
  }
</script>

<div class="tym-page">
  <p class="tym-section-sub mb-4">
    Entitlement buckets and their annual quota. The default type is what the forgot-punch-out sweeper deducts from
    after repeated occurrences.
  </p>
  {#if session.isAdmin}
    <Toolbar>
      {#snippet actions()}
        <button type="button" class="btn-primary" onclick={openCreate}>
          <Plus class="h-4 w-4" /> New leave type
        </button>
      {/snippet}
    </Toolbar>
  {/if}

  {#if query.isLoading}
    <div class="tym-loading-state">Loading...</div>
  {:else if query.isError}
    <div class="tym-error-state">Could not load leave types.</div>
  {:else if query.data}
    <DataTable
      mobileCards
      columns={[
        { key: 'name', label: 'Name', cell: nameCell },
        { key: 'quota', label: 'Quota (days/year)', cell: quotaCell },
        { key: 'default', label: 'Default', cell: defaultCell },
        { key: 'active', label: 'Active', cell: activeCell },
        ...(session.isAdmin ? [{ key: 'actions', label: '', align: 'right' as const, cell: actionsCell }] : [])
      ]}
      rows={query.data}
      rowKey={(lt) => lt.id}
      emptyTitle="No leave types yet"
      emptyBody={session.isAdmin ? 'Add one to start tracking entitlements.' : "HR hasn't configured any yet."}
    />
  {/if}

  {#if showForm && session.isAdmin}
    <Modal title={editing ? 'Edit leave type' : 'New leave type'} onClose={() => (showForm = false)}>
      <div class="space-y-4">
        <div>
          <div class="label">Name</div>
          <input type="text" class="input" aria-label="Name" bind:value={form.name} />
        </div>
        <div>
          <div class="label">Quota (days/year)</div>
          <input type="number" min="0" step="0.5" class="input" aria-label="Quota (days/year)" bind:value={form.days_per_year} />
        </div>
        <label class="flex items-center gap-2 text-[13px] text-toyama-heavy-carbon">
          <input type="checkbox" bind:checked={form.active} class="h-4 w-4 accent-toyama-teal" />
          Active
        </label>
        <label class="flex items-center gap-2 text-[13px] text-toyama-heavy-carbon">
          <input type="checkbox" bind:checked={form.is_deduction_default} class="h-4 w-4 accent-toyama-teal" />
          Deduction default (only one leave type may hold this)
        </label>

        {#if error}<div class="tym-error-state">{error}</div>{/if}
        <div class="flex justify-end gap-2 pt-1">
          <button type="button" class="btn-secondary" onclick={() => (showForm = false)}>Cancel</button>
          <button type="button" class="btn-primary" disabled={saving} onclick={save}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  {/if}
</div>

{#snippet nameCell(lt: LeaveType)}
  <span class="font-medium text-navy">{lt.name}</span>
{/snippet}
{#snippet quotaCell(lt: LeaveType)}
  <span class="muted">{lt.days_per_year}</span>
{/snippet}
{#snippet defaultCell(lt: LeaveType)}
  {#if lt.is_deduction_default}
    <StatusBadge kind="info" label="Default" />
  {:else}
    <span class="muted">—</span>
  {/if}
{/snippet}
{#snippet activeCell(lt: LeaveType)}
  <span class="inline-flex items-center gap-1.5 {lt.active ? 'text-navy' : 'text-dark-gray'}">
    <StatusLamp tone={lt.active ? 'good' : 'idle'} />
    {lt.active ? 'Active' : 'Inactive'}
  </span>
{/snippet}
{#snippet actionsCell(lt: LeaveType)}
  <RowActions items={actionsFor(lt)} />
{/snippet}
